import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AlertModal from './AlertModal';
import { ServerIcon } from '../utils/serverIcons';
import {
  PAYOUT_DISPLAY_METRICS,
  calculatePlanDp,
  normalizeDpPlanSettings,
  aggregateDpPlanMetrics,
  toFiniteNumber,
} from '../utils/dpPlanSettings';
import './StatisticsManagers.css';
import './StatisticsManagersExtended.css';
import './Payouts.css';

const Payouts = () => {
  const activeStatType = 'MRP';
  const [periodNumber, setPeriodNumber] = useState(1);
  const [periodData, setPeriodData] = useState({ start: null, end: null });
  const [moderatorsData, setModeratorsData] = useState([]);
  const [servers, setServers] = useState([]);
  const [mcSettings, setMcSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  // Отдельный процент для каждого типа статистики
  const [globalPercentages, setGlobalPercentages] = useState({
    MRP: 0,
  });
  const [moderatorPercentages, setModeratorPercentages] = useState({});
  const [moderatorBonusCoins, setModeratorBonusCoins] = useState({});
  const [moderatorBonusRp, setModeratorBonusRp] = useState({});
  const [moderatorIssued, setModeratorIssued] = useState({}); // Флажки «выдано»
  const [saving, setSaving] = useState(false);
  /** По серверам: на сколько делить MC при ограничении (ключ — имя сервера, значение — число, 1 = не делить) */
  const [serverDivideBy, setServerDivideBy] = useState({});
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });
  
  // Refs для отслеживания изменений (чтобы не сохранять при начальной загрузке)
  const isInitialLoad = useRef(true);
  const saveTimeout = useRef(null);

  // Первый период: 27.07.2026 — 09.08.2026
  const FIRST_PERIOD_START = new Date(2026, 6, 27);
  const PERIOD_DAYS = 14; // 2 недели

  // Вычисление номера периода и дат
  const calculatePeriod = useCallback((periodNum) => {
    const periodStart = new Date(FIRST_PERIOD_START);
    periodStart.setDate(periodStart.getDate() + (periodNum - 1) * PERIOD_DAYS);
    
    const periodEnd = new Date(periodStart);
    periodEnd.setDate(periodEnd.getDate() + PERIOD_DAYS - 1);
    
    return { start: periodStart, end: periodEnd };
  }, []);

  // Получение текущего периода
  const getCurrentPeriod = useCallback(() => {
    const today = new Date();
    const diffTime = today - FIRST_PERIOD_START;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, Math.floor(diffDays / PERIOD_DAYS) + 1);
  }, []);

  // Форматирование даты
  const formatDate = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Получение дат недель периода
  const getWeekDates = useCallback((periodStart) => {
    // Первая неделя
    const week1Start = new Date(periodStart);
    const week1End = new Date(periodStart);
    week1End.setDate(week1End.getDate() + 6);
    
    // Вторая неделя
    const week2Start = new Date(periodStart);
    week2Start.setDate(week2Start.getDate() + 7);
    const week2End = new Date(week2Start);
    week2End.setDate(week2End.getDate() + 6);
    
    return {
      week1: { start: week1Start, end: week1End },
      week2: { start: week2Start, end: week2End }
    };
  }, []);

  // Проверка доступа
  const canAccessPayouts = () => {
    const allowedRoles = ['Deputy Chief Manager', 'Chief Manager', 'admin'];
    return userRoles.some(role => allowedRoles.includes(role));
  };

  // Загрузка ролей пользователя
  const loadUserRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/check-roles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error loading user roles:', error);
    }
  };

  // Загрузка настроек MC
  const loadMcSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/majestic-coins-settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMcSettings(data);
      }
    } catch (error) {
      console.error('Error loading MC settings:', error);
    }
  };

  // Загрузка серверов
  const loadServers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/servers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setServers(data);
      }
    } catch (error) {
      console.error('Error loading servers:', error);
    }
  };

  // Загрузка настроек выплат из БД
  const loadPayoutSettings = useCallback(async () => {
    if (!periodNumber || !activeStatType) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/payouts/settings?periodNumber=${periodNumber}&statType=${activeStatType}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        // Устанавливаем общий процент для текущего типа статистики
        setGlobalPercentages(prev => ({
          ...prev,
          [activeStatType]: toFiniteNumber(data.globalPercentage, 0)
        }));
        
        // Устанавливаем индивидуальные настройки менеджеров
        if (data.moderatorSettings) {
          const newPercentages = {};
          const newBonusCoins = {};
          const newBonusRp = {};
          const newIssued = {};
          
          Object.entries(data.moderatorSettings).forEach(([discordId, settings]) => {
            newPercentages[discordId] = toFiniteNumber(settings.percentage, 0);
            newBonusCoins[discordId] = toFiniteNumber(settings.bonusCoins, 0);
            newBonusRp[discordId] = toFiniteNumber(settings.bonusRp, 0);
            newIssued[discordId] = !!settings.issued;
          });
          
          // Заменяем настройки полностью (не объединяем)
          setModeratorPercentages(newPercentages);
          setModeratorBonusCoins(newBonusCoins);
          setModeratorBonusRp(newBonusRp);
          setModeratorIssued(newIssued);
        } else {
          // Если настроек нет в БД, сбрасываем в пустые объекты
          setModeratorPercentages({});
          setModeratorBonusCoins({});
          setModeratorBonusRp({});
          setModeratorIssued({});
        }
        
        setSettingsLoaded(true);
      }
    } catch (error) {
      console.error('Error loading payout settings:', error);
    }
  }, [periodNumber, activeStatType]);

  // Сохранение настроек выплат в БД (с debounce)
  const savePayoutSettings = useCallback(async () => {
    if (isInitialLoad.current || !settingsLoaded) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Собираем настройки менеджеров
      const moderatorSettings = {};
      moderatorsData.forEach(mod => {
        moderatorSettings[mod.discord_id] = {
          percentage: toFiniteNumber(moderatorPercentages[mod.discord_id], 0),
          week1Top: null,
          week2Top: null,
          bonusCoins: toFiniteNumber(moderatorBonusCoins[mod.discord_id], 0),
          bonusRp: toFiniteNumber(moderatorBonusRp[mod.discord_id], 0),
          week1TaskPercent: null,
          week2TaskPercent: null,
          issued: !!moderatorIssued[mod.discord_id],
        };
      });

      const response = await fetch('/api/payouts/settings/bulk', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          periodNumber,
          statType: activeStatType,
          globalPercentage: toFiniteNumber(globalPercentages[activeStatType], 0),
          moderatorSettings
        })
      });

      if (!response.ok) {
        console.error('Failed to save payout settings');
      }
    } catch (error) {
      console.error('Error saving payout settings:', error);
    } finally {
      setSaving(false);
    }
  }, [periodNumber, activeStatType, globalPercentages, moderatorPercentages, moderatorBonusCoins, moderatorBonusRp, moderatorIssued, moderatorsData, settingsLoaded]);

  // Debounced save - сохранять через 500мс после последнего изменения
  const debouncedSave = useCallback(() => {
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }
    saveTimeout.current = setTimeout(() => {
      savePayoutSettings();
    }, 500);
  }, [savePayoutSettings]);

  // Загрузка данных статистики для периода
  const loadStatisticsData = async () => {
    if (!periodData.start || !periodData.end) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const weeks = getWeekDates(periodData.start);
      
      // Форматируем даты для API
      const formatDateForApi = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const week1Start = formatDateForApi(weeks.week1.start);
      const week2Start = formatDateForApi(weeks.week2.start);

      // Получаем endpoint в зависимости от типа статистики
      const endpoint = 'statistics';

      // Загружаем данные для обеих недель
      const [week1Response, week2Response] = await Promise.all([
        fetch(`/api/${endpoint}/week/all?weekStartDate=${week1Start}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/${endpoint}/week/all?weekStartDate=${week2Start}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      let week1Data = [];
      let week2Data = [];

      if (week1Response.ok) {
        week1Data = await week1Response.json();
      }
      if (week2Response.ok) {
        week2Data = await week2Response.json();
      }

      // Объединяем данные по модераторам
      const combinedData = combineWeeksData(week1Data, week2Data, weeks);
      setModeratorsData(combinedData);
      
      // НЕ инициализируем здесь проценты и бонусы - они будут загружены из БД в loadPayoutSettings
      
    } catch (error) {
      console.error('Error loading statistics data:', error);
      setAlertModal({
        isOpen: true,
        title: 'Ошибка',
        message: 'Не удалось загрузить данные статистики',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Объединение данных за две недели
  const combineWeeksData = (week1Data, week2Data, weeks) => {
    const moderatorsMap = new Map();
    const getPunishments = (mod) => (
      Number(mod.totalPunishments) ||
      (Number(mod.totalMetrics?.punishments_discord) || 0) +
      (Number(mod.totalMetrics?.punishments_as) || 0)
    );

    // Обработка первой недели
    week1Data.forEach(mod => {
      // Для MEDIA используем totalScreenHours, для остальных - totalMinutes
      const minutes = mod.totalMinutes || 0;
      const hours = mod.totalHours || Math.floor((mod.totalMinutes || 0) / 60);
        
      if (!moderatorsMap.has(mod.discord_id)) {
        moderatorsMap.set(mod.discord_id, {
          discord_id: mod.discord_id,
          nickname: mod.nickname,
          angry_space: mod.angry_space,
          server: mod.server,
          role: mod.role,
          week1Metrics: mod.totalMetrics || {},
          week2Metrics: {},
          week1Minutes: minutes,
          week1Punishments: getPunishments(mod),
          week1ChatResponses: mod.totalChatResponses || 0,
          week1Hours: hours,
          week2Minutes: 0,
          week2Punishments: 0,
          week2ChatResponses: 0,
          week2Hours: 0,
          totalChatResponses: mod.totalChatResponses || 0,
          week1Top: null,
          week2Top: null,
          week1Data: mod.dailyStats || [],
          week2Data: []
        });
      } else {
        const existing = moderatorsMap.get(mod.discord_id);
        existing.week1Metrics = mod.totalMetrics || {};
        existing.week1Minutes = minutes;
        existing.week1Punishments = getPunishments(mod);
        existing.week1ChatResponses = mod.totalChatResponses || 0;
        existing.week1Hours = hours;
        existing.totalChatResponses = mod.totalChatResponses || 0;
        existing.week1Data = mod.dailyStats || [];
      }
    });

    // Обработка второй недели
    week2Data.forEach(mod => {
      // Для MEDIA используем totalScreenHours, для остальных - totalMinutes
      const minutes = mod.totalMinutes || 0;
      const hours = mod.totalHours || Math.floor((mod.totalMinutes || 0) / 60);
        
      if (!moderatorsMap.has(mod.discord_id)) {
        moderatorsMap.set(mod.discord_id, {
          discord_id: mod.discord_id,
          nickname: mod.nickname,
          angry_space: mod.angry_space,
          server: mod.server,
          role: mod.role,
          week1Metrics: {},
          week2Metrics: mod.totalMetrics || {},
          week1Minutes: 0,
          week1Punishments: 0,
          week1Hours: 0,
          week2Minutes: minutes,
          week2Punishments: getPunishments(mod),
          week2ChatResponses: mod.totalChatResponses || 0,
          week2Hours: hours,
          totalChatResponses: mod.totalChatResponses || 0,
          week1Top: null,
          week2Top: null,
          week1Data: [],
          week2Data: mod.dailyStats || []
        });
      } else {
        const existing = moderatorsMap.get(mod.discord_id);
        existing.week2Metrics = mod.totalMetrics || {};
        existing.week2Minutes = minutes;
        existing.week2Punishments = getPunishments(mod);
        existing.week2ChatResponses = mod.totalChatResponses || 0;
        existing.week2Hours = hours;
        existing.totalChatResponses = (existing.week1ChatResponses || 0) + (mod.totalChatResponses || 0);
        existing.week2Data = mod.dailyStats || [];
      }
    });

    const moderatorsArray = Array.from(moderatorsMap.values());

    return moderatorsArray;
  };

  const sumMetrics = (week1Metrics = {}, week2Metrics = {}) => {
    const merged = {};
    const rawKeys = [
      'punishments_discord',
      'punishments_as',
      'punishments',
      'chat_responses_discord',
      'chat_responses_as',
      'chat_responses',
      'deleted_comments_as',
      'closed_tickets',
    ];
    rawKeys.forEach((key) => {
      const a = week1Metrics?.[key];
      const b = week2Metrics?.[key];
      if (a != null || b != null) {
        merged[key] = (Number(a) || 0) + (Number(b) || 0);
      }
    });
    return aggregateDpPlanMetrics(merged);
  };

  // DP: планы Минимум / Максимум закрываются только полностью
  const calculateMC = (moderator) => {
    if (!mcSettings) return 0;

    const settings = normalizeDpPlanSettings(mcSettings[activeStatType] || mcSettings.MRP || {});
    const metrics = sumMetrics(moderator.week1Metrics, moderator.week2Metrics);
    let totalMC = calculatePlanDp(metrics, settings);

    const individualPercent = toFiniteNumber(moderatorPercentages[moderator.discord_id], 0);
    const globalPercent = toFiniteNumber(globalPercentages[activeStatType], 0);
    const totalPercent = globalPercent + individualPercent;

    if (totalPercent !== 0) {
      totalMC = Math.round(totalMC * (1 + totalPercent / 100));
    }

    const bonusCoins = toFiniteNumber(moderatorBonusCoins[moderator.discord_id], 0);
    totalMC += bonusCoins;

    return Math.max(0, totalMC);
  };

  // MC с учётом деления по серверу
  const getMCWithDivide = (moderator, serverName) => {
    const mc = calculateMC(moderator);
    const div = serverDivideBy[serverName] || 1;
    return div > 1 ? Math.floor(mc / div) : mc;
  };

  // Навигация по периодам
  const goToPreviousPeriod = () => {
    if (periodNumber > 1) {
      setPeriodNumber(prev => prev - 1);
    }
  };

  const goToNextPeriod = () => {
    setPeriodNumber(prev => prev + 1);
  };

  const goToCurrentPeriod = () => {
    setPeriodNumber(getCurrentPeriod());
  };

  // Группировка модераторов по серверам
  const groupByServer = (moderators) => {
    const groups = {};
    moderators.forEach(mod => {
      const server = mod.server || 'Без сервера';
      if (!groups[server]) {
        groups[server] = [];
      }
      groups[server].push(mod);
    });
    
    // Сортируем серверы по номеру
    return Object.entries(groups).sort((a, b) => {
      const numA = parseInt(a[0].match(/\d+/)?.[0] || '999');
      const numB = parseInt(b[0].match(/\d+/)?.[0] || '999');
      return numA - numB;
    });
  };

  // Показ числа в инпуте: 0/пусто → '', чтобы сразу писать цифру или "-"
  const editableNumberValue = (val) => {
    if (val === '' || val === '-') return val;
    if (val === null || val === undefined || val === 0) return '';
    return String(val);
  };

  const normalizeNumberOnBlur = (val, setter, key = null) => {
    const num = (val === '' || val === '-' || val === null || val === undefined)
      ? 0
      : (parseInt(val, 10) || 0);
    if (key !== null) {
      setter((prev) => ({ ...prev, [key]: num }));
    } else {
      setter(num);
    }
  };

  // Обработчик изменения процента (поддержка - и +)
  const handlePercentChange = (value, setter, key = null) => {
    let cleanValue = value.replace(/[^\d-]/g, '');
    if (cleanValue.includes('-')) {
      cleanValue = `-${cleanValue.replace(/-/g, '')}`;
      if (cleanValue === '-') {
        // только минус — оставляем для ввода отрицательного
      }
    }

    if (cleanValue === '' || cleanValue === '-') {
      if (key !== null) {
        setter((prev) => ({ ...prev, [key]: cleanValue }));
      } else {
        setter(cleanValue);
      }
      return;
    }

    const numValue = parseInt(cleanValue, 10);
    if (!isNaN(numValue)) {
      if (key !== null) {
        setter((prev) => ({ ...prev, [key]: numValue }));
      } else {
        setter(numValue);
      }
    }
  };

  // Эффекты
  useEffect(() => {
    loadUserRoles();
    loadMcSettings();
    loadServers();
    setPeriodNumber(getCurrentPeriod());
  }, [getCurrentPeriod]);

  useEffect(() => {
    const period = calculatePeriod(periodNumber);
    setPeriodData(period);
  }, [periodNumber, calculatePeriod]);

  useEffect(() => {
    if (periodData.start && mcSettings) {
      // При смене периода или типа статистики - сбрасываем флаг загрузки настроек
      setSettingsLoaded(false);
      isInitialLoad.current = true;
      // Полностью очищаем настройки модераторов при смене типа статистики
      setModeratorPercentages({});
      setModeratorBonusCoins({});
      setModeratorBonusRp({});
      setModeratorIssued({});
      loadStatisticsData();
    }
  }, [periodData, activeStatType, mcSettings]);

  // Загрузка настроек выплат после загрузки данных модераторов
  useEffect(() => {
    if (moderatorsData.length > 0 && !settingsLoaded) {
      loadPayoutSettings().then(() => {
        // После загрузки настроек разрешаем автосохранение
        setTimeout(() => {
          isInitialLoad.current = false;
        }, 100);
      });
    }
  }, [moderatorsData, settingsLoaded, loadPayoutSettings]);

  // Автосохранение при изменении настроек
  useEffect(() => {
    if (!isInitialLoad.current && settingsLoaded && moderatorsData.length > 0) {
      debouncedSave();
    }
    
    // Очистка таймера при размонтировании
    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, [globalPercentages, moderatorPercentages, moderatorBonusCoins, moderatorBonusRp, moderatorIssued, debouncedSave, settingsLoaded, moderatorsData]);

  // Вычисляем недели на основе periodNumber, если periodData.start еще не загружен
  const weeks = useMemo(() => {
    if (periodData.start) {
      return getWeekDates(periodData.start);
    }
    // Если periodData.start еще не загружен, вычисляем на основе periodNumber
    const periodStart = calculatePeriod(periodNumber).start;
    return getWeekDates(periodStart);
  }, [periodData.start, periodNumber, getWeekDates, calculatePeriod]);
  
  const groupedModerators = groupByServer(moderatorsData);

  const serverIconMap = useMemo(() => {
    const m = {};
    servers.forEach((s) => {
      m[`RU${s.server_number}_${s.server_name}`] = s.icon_url || null;
    });
    return m;
  }, [servers]);

  // Подсчет итогов
  const totals = useMemo(() => {
    let totalMC = 0;
    let totalRp = 0;
    let totalModerators = 0;
    
    moderatorsData.forEach(mod => {
      const mc = calculateMC(mod);
      const rp = toFiniteNumber(moderatorBonusRp[mod.discord_id], 0);
      totalRp += rp;
      if (mc > 0 || rp !== 0) {
        totalMC += mc;
        totalModerators++;
      }
    });
    
    return { totalMC, totalRp, totalModerators };
  }, [moderatorsData, globalPercentages, moderatorPercentages, moderatorBonusCoins, moderatorBonusRp, mcSettings, activeStatType]);

  // Проверка доступа
  if (!canAccessPayouts() && userRoles.length > 0) {
    return (
      <div className="payouts-access-denied">
        <h2>Доступ запрещён</h2>
        <p>У вас нет прав для просмотра этой страницы.</p>
        <p className="access-hint">Требуется роль: Deputy Chief Manager, Chief Manager, admin</p>
      </div>
    );
  }

  return (
    <div className="payouts-container">
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />

      {/* Компактный header с навигацией и табами */}
      <div className="payouts-header">
        <div className="payouts-header-top">
          <div className="period-block">
            <button 
              className="period-arrow"
              onClick={goToPreviousPeriod}
              disabled={periodNumber <= 1}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <div className="period-info">
              <span className="period-number">Период #{periodNumber}</span>
              <span className="period-dates">
                {formatDate(weeks?.week1?.start)} — {formatDate(weeks?.week2?.end)}
              </span>
            </div>
            <button 
              className="period-arrow"
              onClick={goToNextPeriod}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
            {periodNumber !== getCurrentPeriod() && (
              <button className="period-today" onClick={goToCurrentPeriod}>
                Сегодня
              </button>
            )}
          </div>
          
          <div className="header-right">
            <div className="global-percent-inline">
              <span className="global-percent-label">Общий %</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={editableNumberValue(globalPercentages[activeStatType])}
                onChange={(e) => handlePercentChange(e.target.value, setGlobalPercentages, activeStatType)}
                onFocus={(e) => e.target.select()}
                onBlur={() => normalizeNumberOnBlur(globalPercentages[activeStatType], setGlobalPercentages, activeStatType)}
                className="global-percent-input"
              />
            </div>
            {saving ? (
              <div className="save-status saving">
                <span className="save-dot"></span>
                Сохранение
              </div>
            ) : settingsLoaded && (
              <div className="save-status saved">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
            )}
          </div>
        </div>

        <div className="payouts-stats-bar">
        <div className="stat-item stat-item-mc">
          <div className="stat-value-wrapper">
            <span className="stat-value">{totals.totalMC.toLocaleString()}</span>
            <span className="stat-unit">DP</span>
          </div>
          <span className="stat-label">к выдаче</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item stat-item-rp">
          <div className="stat-value-wrapper">
            <span className="stat-value">{totals.totalRp.toLocaleString()}</span>
            <span className="stat-unit">RP</span>
          </div>
          <span className="stat-label">бонус</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <span className="stat-value">{totals.totalModerators}</span>
          <span className="stat-label">менеджеров</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item stat-item-week">
          <div className="week-dates">
            <span className="week-label">Неделя 1</span>
            <span className="week-value">{formatDate(weeks?.week1?.start)} — {formatDate(weeks?.week1?.end)}</span>
          </div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item stat-item-week">
          <div className="week-dates">
            <span className="week-label">Неделя 2</span>
            <span className="week-value">{formatDate(weeks?.week2?.start)} — {formatDate(weeks?.week2?.end)}</span>
          </div>
        </div>
        </div>
      </div>

      {loading && (
        <div className="payouts-loading">
          <div className="loader"></div>
        </div>
      )}

      {!loading && groupedModerators.map(([serverName, serverModerators]) => (
        <div key={serverName} className="payouts-server-section">
          <div className="server-header">
            <div className="server-info">
              <ServerIcon iconUrl={serverIconMap[serverName]} size={24} className="payouts-server-icon" />
              <div className="server-identity">
                <span className="server-name">{serverName}</span>
                <span className="server-stats">
                  {serverModerators.length} чел. ·{' '}
                  {serverModerators.reduce((sum, m) => sum + getMCWithDivide(m, serverName), 0).toLocaleString()} DP ·{' '}
                  {serverModerators.reduce((sum, m) => sum + toFiniteNumber(moderatorBonusRp[m.discord_id], 0), 0).toLocaleString()} RP
                </span>
              </div>
            </div>
            <div className="server-header-divide">
              <span className="server-divide-label">Разделить на</span>
              <input
                type="text"
                inputMode="numeric"
                value={editableNumberValue(serverDivideBy[serverName] ?? 1)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d]/g, '');
                  if (raw === '') {
                    setServerDivideBy((prev) => ({ ...prev, [serverName]: '' }));
                    return;
                  }
                  const v = Math.max(1, parseInt(raw, 10) || 1);
                  setServerDivideBy((prev) => ({ ...prev, [serverName]: v }));
                }}
                onFocus={(e) => e.target.select()}
                onBlur={() => {
                  const cur = serverDivideBy[serverName];
                  const v = Math.max(1, parseInt(cur, 10) || 1);
                  setServerDivideBy((prev) => ({ ...prev, [serverName]: v }));
                }}
                placeholder="1"
                className="server-divide-input"
                title="Делить DP для всех на этом сервере на указанное число (при ограничении)"
              />
            </div>
          </div>

          <section className="mrp-board payouts-board">
            <div className="mrp-board__frame">
              <div className="mrp-board__scroll payouts-table-wrapper">
                <table className="mrp-sheet payouts-table">
                  <thead>
                    <tr>
                      <th className="mrp-h po-h--manager">Менеджер</th>
                      <th className="mrp-h po-h--metrics">Неделя 1</th>
                      <th className="mrp-h po-h--metrics">Неделя 2</th>
                      <th className="mrp-h po-h--metrics">Итог по данным</th>
                      <th className="mrp-h po-h--settings">Настройки</th>
                      <th className="mrp-h po-h--payout">Выдать</th>
                      <th className="mrp-h po-h--issued">Выдано</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serverModerators.map(moderator => {
                      const mc = getMCWithDivide(moderator, serverName);
                      const bonusRp = toFiniteNumber(moderatorBonusRp[moderator.discord_id], 0);
                      const totalMetrics = sumMetrics(moderator.week1Metrics, moderator.week2Metrics);
                      const isIssued = !!moderatorIssued[moderator.discord_id];

                      const renderMetrics = (metrics) => {
                        const display = aggregateDpPlanMetrics(metrics || {});
                        return (
                          <div className="po-metrics">
                            {PAYOUT_DISPLAY_METRICS.map((metric) => {
                              const value = Number(display[metric.key]) || 0;
                              return (
                                <div key={metric.key} className={`po-metric po-metric--${metric.group}`} title={metric.label}>
                                  <span className="po-metric__label">{metric.short}</span>
                                  <span className={`po-metric__value ${value > 0 ? 'has-value' : ''}`}>
                                    {value > 0 ? value : '—'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      };

                      return (
                        <tr key={moderator.discord_id} className={isIssued ? 'issued-row' : ''}>
                          <td className="mrp-c po-manager">
                            <div className="po-manager__stack">
                              <span className="mrp-nick po-manager__nick">{moderator.nickname || '—'}</span>
                              <span className="po-manager__as">AS · {moderator.angry_space || '—'}</span>
                            </div>
                          </td>
                          <td className="mrp-c po-metrics-cell">{renderMetrics(moderator.week1Metrics)}</td>
                          <td className="mrp-c po-metrics-cell">{renderMetrics(moderator.week2Metrics)}</td>
                          <td className="mrp-c po-metrics-cell po-metrics-cell--total">{renderMetrics(totalMetrics)}</td>
                          <td className="mrp-c settings-cell">
                            <div className="po-settings">
                              <label className="po-setting">
                                <span className="po-setting__label">Инд. %</span>
                                <div className="po-setting__input">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="0"
                                    value={editableNumberValue(moderatorPercentages[moderator.discord_id])}
                                    onChange={(e) => handlePercentChange(e.target.value, setModeratorPercentages, moderator.discord_id)}
                                    onFocus={(e) => e.target.select()}
                                    onBlur={() => normalizeNumberOnBlur(moderatorPercentages[moderator.discord_id], setModeratorPercentages, moderator.discord_id)}
                                    className="input-field"
                                    aria-label={`Индивидуальный процент ${moderator.nickname}`}
                                  />
                                </div>
                              </label>
                              <label className="po-setting">
                                <span className="po-setting__label">Бонус DP</span>
                                <div className="po-setting__input">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="0"
                                    value={editableNumberValue(moderatorBonusCoins[moderator.discord_id])}
                                    onChange={(e) => handlePercentChange(e.target.value, setModeratorBonusCoins, moderator.discord_id)}
                                    onFocus={(e) => e.target.select()}
                                    onBlur={() => normalizeNumberOnBlur(moderatorBonusCoins[moderator.discord_id], setModeratorBonusCoins, moderator.discord_id)}
                                    className="input-field"
                                    aria-label={`Бонус DP ${moderator.nickname}`}
                                  />
                                </div>
                              </label>
                              <label className="po-setting">
                                <span className="po-setting__label">Бонус RP</span>
                                <div className="po-setting__input">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="0"
                                    value={editableNumberValue(moderatorBonusRp[moderator.discord_id])}
                                    onChange={(e) => handlePercentChange(e.target.value, setModeratorBonusRp, moderator.discord_id)}
                                    onFocus={(e) => e.target.select()}
                                    onBlur={() => normalizeNumberOnBlur(moderatorBonusRp[moderator.discord_id], setModeratorBonusRp, moderator.discord_id)}
                                    className="input-field"
                                    aria-label={`Бонус RP ${moderator.nickname}`}
                                  />
                                </div>
                              </label>
                            </div>
                          </td>
                          <td className="mrp-c total-cell">
                            <div className="po-payout-total" title="Сколько выдать">
                              <div className={`po-payout-line po-payout-line--dp ${mc > 0 ? 'has-value' : ''}`}>
                                <span className="po-payout-line__tag">DP</span>
                                <span className="po-payout-line__amount">{mc.toLocaleString()}</span>
                              </div>
                              <div className={`po-payout-line po-payout-line--rp ${bonusRp !== 0 ? 'has-value' : ''}`}>
                                <span className="po-payout-line__tag">RP</span>
                                <span className="po-payout-line__amount">{bonusRp.toLocaleString()}</span>
                              </div>
                            </div>
                          </td>
                          <td className="mrp-c issued-cell">
                            <button
                              type="button"
                              className={`issued-toggle ${isIssued ? 'is-on' : ''}`}
                              onClick={() => setModeratorIssued((prev) => ({
                                ...prev,
                                [moderator.discord_id]: !prev[moderator.discord_id]
                              }))}
                              aria-pressed={isIssued}
                              title={isIssued ? 'Отметить как не выдано' : 'Отметить как выдано'}
                            >
                              <span className="issued-toggle-icon" aria-hidden="true">
                                {isIssued ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <circle cx="12" cy="12" r="8" />
                                  </svg>
                                )}
                              </span>
                              <span className="issued-toggle-text">{isIssued ? 'Выдано' : 'Не выдано'}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      ))}

      {!loading && moderatorsData.length === 0 && (
        <div className="payouts-empty">
          <h3>Нет данных</h3>
          <p>Нет данных о статистике менеджеров за выбранный период</p>
        </div>
      )}
    </div>
  );
};

export default Payouts;
